<script setup lang="ts">
import { useUserStore } from '@blueprint/frontend-basics'
import { inject, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { ensureAdminAccess } from '@/router/guards/admin.guard.ts'
import type { PlatformUserStatsDto } from '@/models/dtos/admin.dto.ts'
import type { ShopDto } from '@/models/dtos/shop.dto.ts'
import type { useServiceStore } from '@/stores/service.store.ts'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const userStore = useUserStore()
const serviceStore = inject<ReturnType<typeof useServiceStore>>('serviceStore')

const canViewDashboard = ref(false)

const stats = ref<PlatformUserStatsDto | null>(null)
const shops = ref<ShopDto[]>([])
const page = ref(0)
const pageSize = 20
const totalShops = ref(0)
const statsLoading = ref(true)
const shopsLoading = ref(false)
const loadError = ref<string | null>(null)
const hasMoreShops = ref(true)
const shopsInitialized = ref(false)

const loadStats = async (): Promise<void> => {
  if (!serviceStore?.adminService) {
    return
  }
  statsLoading.value = true
  try {
    stats.value = await serviceStore.adminService.getPlatformUserStats()
  } catch {
    loadError.value = t('dashboard.loadError')
  } finally {
    statsLoading.value = false
  }
}

const loadNextShopsPage = async (): Promise<void> => {
  if (!serviceStore?.adminService || shopsLoading.value || !hasMoreShops.value) {
    return
  }
  shopsLoading.value = true
  try {
    const result = await serviceStore.adminService.listShops(
      page.value,
      pageSize,
    )
    shops.value.push(...result.items)
    totalShops.value = result.total
    page.value += 1
    hasMoreShops.value = shops.value.length < result.total
  } catch {
    loadError.value = t('dashboard.loadError')
    hasMoreShops.value = false
  } finally {
    shopsLoading.value = false
    shopsInitialized.value = true
  }
}

const loadMoreShops = async ({
  done,
}: {
  done: (status: 'ok' | 'empty' | 'error') => void
}): Promise<void> => {
  if (!hasMoreShops.value) {
    done('empty')
    return
  }
  try {
    await loadNextShopsPage()
    done(hasMoreShops.value ? 'ok' : 'empty')
  } catch {
    done('error')
  }
}

onMounted(async () => {
  const lang = (route.params.lang as string) ?? 'en'
  const allowed = await ensureAdminAccess(router, lang)
  if (!allowed) {
    return
  }
  canViewDashboard.value = true
  await loadStats()
  await loadNextShopsPage()
})
</script>

<template>
  <v-container v-if="userStore.isLoading || !canViewDashboard" class="py-6">
    <div class="text-center py-12">
      <v-progress-circular indeterminate color="primary" size="48" />
      <p class="text-medium-emphasis mt-4">
        {{ t('general.loading') }}
      </p>
    </div>
  </v-container>
  <v-container v-else class="py-6">
    <h1 class="text-h4 mb-6">
      {{ t('dashboard.title') }}
    </h1>

    <v-alert
      v-if="loadError"
      type="error"
      class="mb-4"
      :text="loadError"
    />

    <v-row class="mb-6">
      <v-col cols="12" sm="6" lg="3">
        <v-card variant="outlined">
          <v-card-title>{{ t('dashboard.anonymousUsers') }}</v-card-title>
          <v-card-text class="text-h5">
            <v-progress-circular
              v-if="statsLoading"
              indeterminate
              size="24"
            />
            <span v-else>{{ stats?.anonymousUserCount ?? '—' }}</span>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card variant="outlined">
          <v-card-title>{{ t('dashboard.loggedInUsers') }}</v-card-title>
          <v-card-text class="text-h5">
            <v-progress-circular
              v-if="statsLoading"
              indeterminate
              size="24"
            />
            <span v-else>{{ stats?.loggedInUserCount ?? '—' }}</span>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card variant="outlined">
          <v-card-title>{{ t('dashboard.shops') }}</v-card-title>
          <v-card-text class="text-h5">
            <v-progress-circular
              v-if="statsLoading"
              indeterminate
              size="24"
            />
            <span v-else>{{ stats?.shopCount ?? '—' }}</span>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-card variant="outlined">
          <v-card-title>{{ t('dashboard.teams') }}</v-card-title>
          <v-card-text class="text-h5">
            <v-progress-circular
              v-if="statsLoading"
              indeterminate
              size="24"
            />
            <span v-else>{{ stats?.teamCount ?? '—' }}</span>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <h2 class="text-h5 mb-4">
      {{ t('dashboard.shopsHeading') }}
      <span v-if="totalShops > 0" class="text-medium-emphasis text-body-2">
        ({{ shops.length }} / {{ totalShops }})
      </span>
    </h2>

    <div
      v-if="shopsLoading && shops.length === 0"
      class="text-center py-8"
    >
      <v-progress-circular indeterminate color="primary" />
    </div>

    <v-row v-if="shops.length > 0">
      <v-col
        v-for="shop in shops"
        :key="shop.id"
        cols="12"
        sm="6"
        md="4"
      >
        <v-card variant="elevated">
          <v-card-title>{{ shop.name }}</v-card-title>
          <v-card-subtitle>{{ t('dashboard.shopId') }}: {{ shop.id }}</v-card-subtitle>
          <v-card-text v-if="shop.creationDate">
            {{ t('dashboard.created') }}: {{ shop.creationDate }}
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-infinite-scroll
      v-if="shops.length > 0 && hasMoreShops"
      @load="loadMoreShops"
    >
      <template #loading>
        <div class="text-center py-4">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </template>
      <template #empty />
    </v-infinite-scroll>

    <p
      v-else-if="shopsInitialized && !shopsLoading && shops.length === 0"
      class="text-medium-emphasis"
    >
      {{ t('dashboard.noShops') }}
    </p>
  </v-container>
</template>
